import {
  Children,
  cloneElement,
  createContext,
  CSSProperties,
  FC,
  FormHTMLAttributes,
  HTMLAttributes,
  isValidElement,
  ReactNode,
  RefObject,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import classNames from 'classnames';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';

import { Button } from '../Button';
import { Headline3 } from '../Headline';
import { Close } from '../Icons';
import { OverlayContext } from '../Overlay/OverlayContext';
import { DismissReason, registerOverlay } from '../Overlay/overlayStack';
import { Text } from '../Text';

import styles from './modal.module.scss';

let nextDialogId = 0;
let nextFormId = 0;
const DialogContext = createContext<{
  titleId: string;
  descriptionId: string;
  footerHost: HTMLElement | null;
  busy: boolean;
}>({ titleId: '', descriptionId: '', footerHost: null, busy: false });
const DialogBusyContext = createContext<((busy: boolean) => void) | null>(null);

/** Lets a child editor protect its owning dialog during a mutation. */
const useDialogBusy = (busy: boolean): void => {
  const setBusy = useContext(DialogBusyContext);
  useLayoutEffect(() => {
    setBusy?.(busy);
    return () => setBusy?.(false);
  }, [busy, setBusy]);
};

interface IDialogHeaderProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title?: ReactNode;
  description?: ReactNode;
  closeButton?: ReactNode;
}

const DialogHeader: FC<IDialogHeaderProps> = ({
  title,
  description,
  closeButton,
  children,
  className,
  ...props
}) => {
  const { titleId, descriptionId } = useContext(DialogContext);
  return (
    <header className={classNames(styles.header, className)} {...props}>
      {(title || closeButton) && (
        <div className={styles.headerRow}>
          <div className={styles.headerContent}>
            {title && (
              <div id={titleId}>
                <Headline3>{title}</Headline3>
              </div>
            )}
          </div>
          {closeButton}
        </div>
      )}
      {description && (
        <div id={descriptionId} className={styles.description}>
          <Text color="var(--sk-text-subtle)">{description}</Text>
        </div>
      )}
      {children}
    </header>
  );
};

const DialogBody: FC<HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
  <div className={classNames(styles.body, className)} {...props} />
);

/** Shared spacing for grouped dialog content, including editors rendered by children. */
const DialogStack: FC<HTMLAttributes<HTMLDivElement> & { compact?: boolean }> = ({
  className,
  compact = false,
  ...props
}) => (
  <div
    className={classNames(styles.stack, { [styles.compact]: compact }, className)}
    {...props}
  />
);

const DialogForm: FC<FormHTMLAttributes<HTMLFormElement>> = ({ className, ...props }) => (
  <form className={classNames(styles.stack, className)} {...props} />
);
const linkSubmitButtons = (
  children: ReactNode,
  formId?: string,
  busy = false,
): ReactNode =>
  Children.map(children, child => {
    if (!isValidElement(child)) return child;
    const isSubmit =
      (child.type === Button || child.type === 'button') && child.props.type === 'submit';
    return cloneElement(child, {
      ...((child.type === Button || child.type === 'button') && busy
        ? { disabled: true }
        : {}),
      ...(isSubmit && formId ? { form: child.props.form ?? formId } : {}),
      ...(child.props.children
        ? { children: linkSubmitButtons(child.props.children, formId, busy) }
        : {}),
    });
  });

/** Can live inside a form; actions render outside the scrollable dialog body. */
const DialogFooter: FC<HTMLAttributes<HTMLElement>> = ({
  className,
  children,
  ...props
}) => {
  const { footerHost, busy } = useContext(DialogContext);
  const [generatedFormId] = useState(() => {
    nextFormId += 1;
    return `dialog-form-${nextFormId}`;
  });
  const placeholderRef = useRef<HTMLSpanElement>(null);
  const [formId, setFormId] = useState<string>();
  useLayoutEffect(() => {
    const form = placeholderRef.current?.closest('form');
    if (form) {
      if (!form.id) form.id = generatedFormId;
      setFormId(form.id);
    }
  }, [generatedFormId]);
  const footer = (
    <footer className={classNames(styles.footer, className)} {...props}>
      {linkSubmitButtons(children, formId, busy)}
    </footer>
  );
  return (
    <>
      <span ref={placeholderRef} hidden />
      {footerHost ? createPortal(footer, footerHost) : footer}
    </>
  );
};

interface IDialogProps {
  isOpen: boolean;
  onClose: (reason: DismissReason) => void;
  title?: ReactNode;
  description?: ReactNode;
  header?: ReactNode | false;
  footer?: ReactNode;
  children?: ReactNode;
  size?: 'small' | 'medium' | 'large' | 'wide' | 'fullscreen';
  showCloseButton?: boolean;
  dismissOnEscape?: boolean;
  dismissOnBackdrop?: boolean;
  busy?: boolean;
  initialFocusRef?: RefObject<HTMLElement>;
  returnFocusRef?: RefObject<HTMLElement>;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  className?: string;
  bodyClassName?: string;
  style?: CSSProperties;
}

const Dialog: FC<IDialogProps> = ({
  isOpen,
  onClose,
  title,
  description,
  header,
  footer,
  children,
  size = 'small',
  showCloseButton = true,
  dismissOnEscape = true,
  dismissOnBackdrop = false,
  busy = false,
  initialFocusRef,
  returnFocusRef,
  className,
  bodyClassName,
  style,
  'aria-label': ariaLabel,
  'aria-labelledby': labelledBy,
  'aria-describedby': describedBy,
}) => {
  const intl = useIntl();
  const parentId = useContext(OverlayContext);
  const [layerId] = useState<object>(() => ({}));
  const [id] = useState(() => {
    nextDialogId += 1;
    return `dialog-${nextDialogId}`;
  });
  const rootRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const [footerHost, setFooterHost] = useState<HTMLDivElement | null>(null);
  const [childBusy, setChildBusy] = useState(false);
  const isBusy = busy || childBusy;
  const dismissRef = useRef<(reason: DismissReason) => void>(() => undefined);
  dismissRef.current = reason => {
    if (
      isBusy ||
      (reason === 'escape' && !dismissOnEscape) ||
      (reason === 'backdrop' && !dismissOnBackdrop)
    )
      return;
    onClose(reason);
  };
  useLayoutEffect(() => {
    if (!isOpen || !rootRef.current || !dialogRef.current) return undefined;
    return registerOverlay({
      id: layerId,
      parentId,
      element: dialogRef.current,
      root: rootRef.current,
      modal: true,
      initialFocus: initialFocusRef?.current,
      restoreTarget: returnFocusRef?.current,
      onDismiss: reason => dismissRef.current(reason),
    });
  }, [isOpen, layerId, parentId, initialFocusRef, returnFocusRef]);
  const titleId = `${id}-title`;
  const descriptionId = `${id}-description`;
  const contextValue = useMemo(
    () => ({ titleId, descriptionId, footerHost, busy: isBusy }),
    [titleId, descriptionId, footerHost, isBusy],
  );
  const hasHeader = header !== false && (header !== undefined || title || description);
  const customHeader =
    isValidElement<IDialogHeaderProps>(header) && header.type === DialogHeader
      ? header.props
      : undefined;
  if (!isOpen) return null;
  const closeButton = showCloseButton ? (
    <Button
      type="button"
      icon={<Close />}
      mode="tertiary"
      color="dark"
      className={styles.closeButton}
      disabled={isBusy}
      aria-label={intl.formatMessage({ id: 'common.close' })}
      onClick={() => dismissRef.current('close-button')}
    />
  ) : null;
  let dialogHeader = (
    <DialogHeader title={title} description={description} closeButton={closeButton} />
  );
  if (isValidElement<IDialogHeaderProps>(header) && header.type === DialogHeader) {
    dialogHeader = cloneElement(header, { closeButton });
  } else if (header !== undefined) {
    dialogHeader = (
      <div className={classNames(styles.header, styles.headerRow)}>
        <div className={styles.headerContent}>{header}</div>
        {closeButton}
      </div>
    );
  }
  return createPortal(
    <OverlayContext.Provider value={layerId}>
      <DialogContext.Provider value={contextValue}>
        <DialogBusyContext.Provider value={setChildBusy}>
          <div
            ref={rootRef}
            className={styles.overlay}
            onClick={event => {
              event.stopPropagation();
              if (event.target === event.currentTarget) dismissRef.current('backdrop');
            }}
          >
            <div
              ref={dialogRef}
              role="dialog"
              aria-modal="true"
              aria-busy={isBusy || undefined}
              aria-labelledby={
                labelledBy ??
                (hasHeader && (customHeader?.title ?? title) ? titleId : undefined)
              }
              aria-describedby={
                describedBy ??
                (hasHeader && (customHeader?.description ?? description)
                  ? descriptionId
                  : undefined)
              }
              aria-label={
                labelledBy || (hasHeader && (customHeader?.title ?? title))
                  ? undefined
                  : ariaLabel ?? intl.formatMessage({ id: 'common.dialog' })
              }
              tabIndex={-1}
              className={classNames(styles.modal, styles[size], className)}
              style={style}
            >
              {hasHeader
                ? dialogHeader
                : closeButton && (
                    <div className={styles.standaloneClose}>{closeButton}</div>
                  )}
              <DialogBody className={bodyClassName}>{children}</DialogBody>
              <div ref={setFooterHost} className={styles.footerHost}>
                {footer && (
                  <footer className={styles.footer}>
                    {linkSubmitButtons(footer, undefined, isBusy)}
                  </footer>
                )}
              </div>
            </div>
          </div>
        </DialogBusyContext.Provider>
      </DialogContext.Provider>
    </OverlayContext.Provider>,
    document.body,
  );
};

export { Dialog, DialogHeader, DialogFooter, DialogStack, DialogForm, useDialogBusy };
export type { IDialogProps };
